/* eslint-disable max-classes-per-file */
import jsep from 'jsep';
import cuid from 'cuid';
import Immutable from 'immutable';
import validator from 'validator';
import { rulesCache, get, isValidDateString } from 'ctm-util-functions';

// @todo: eventually drive this off env vars or serverConfig or something
// If this is true, then elementRule, stateValues and rulesResults will be added to the rules payload as well as the
// rules cache
const DEBUG_MODE = false;

/**
 * RulesModel for evaluating rule expressions.
 */
class RulesModel {

    constructor(schema, customEvaluators = {}) {
        this.schema = schema;
        // eslint-disable-next-line no-use-before-define
        this.rulesFactory = new RuleFactory(customEvaluators);
        this.rules = this.rulesFactory.getRules(this.schema);
    }

    /**
     * Extracts the individual rule names from ruleExpr and adds the corresponding
     * rule object to an array that is returned
     * @param  {String} ruleExpr -  A rule rule expression, this can be a single rule,
     *                              or a compound rule made up of multiple rules and
     *                              operators eg "rule", "rule 1 && rule2", ""(rule1 && rule2) || (rule3 && !rule3)""
     * @param {Object} selectedRules - An optional map of selectedRules - {conditionalRule: resultRule}. These are required when
     * getting stateValues, but not a different function is used when evaluating these rules.
     * @returns {Array}           An array of the rule objects that correspond to the rules defined in ruleExpr
     */
    extractRules = (ruleExpr, selectedRules = null) => {
        const rules = [];
        const ruleAST = jsep(ruleExpr);

        // ruleExpr is a single expression
        if (!ruleAST.left) {
            return this.extractSingleRuleFromAST(ruleAST, ruleExpr);
        }

        // ruleExpr is a compound expression
        const exprs = [ruleAST.left, ruleAST.right];
        while (exprs.length > 0) {
            const exp = exprs.shift();
            // @todo: cleanup this logic a bit - it'll do for now, but could be better
            if ('name' in exp) {
                if (exp.name === 'selectedRules') {
                    if (selectedRules) {
                        const __selectedRules = Object.keys(selectedRules).reduce((acc, val) => {
                            acc.push(...[...this.extractRules(val), ...this.extractRules(selectedRules[val])]);
                            return acc;
                        }, []);
                        rules.push(...__selectedRules);
                    }
                    continue;
                }
                // @todo: check that the rule is not already in rules.
                if (this.rules[exp.name] !== undefined) {
                    rules.push(this.rules[exp.name]);
                } else {
                    throw Error(`Rule name ${exp.name} is not in the rules schema. ${ruleExpr}`);
                }
            } else if ('argument' in exp) {
                if (this.rules[exp.argument.name] !== undefined) {
                    rules.push(this.rules[exp.argument.name]);
                } else {
                    throw Error(`Rule name ${exp.name} - ${ruleExpr} is not in the rules schema. ${ruleExpr}`);
                }
            }
            if ('left' in exp) {
                exprs.push(exp.left);
            }
            if ('right' in exp) {
                exprs.push(exp.right);
            }
        }
        return rules;
    };

    /**
     * Extracts the name of a rule from a single rule AST and returns the corresponding rule object.
     * @param  {String} ruleAST  A rule AST for a single rule.
     * @param {String} ruleExpr the rule expression
     * @returns {Array}           A single element array containing the rule object that matches the rule name in ruleAST.
     */
    extractSingleRuleFromAST = (ruleAST, ruleExpr) => {
        // ruleExpr is a unary expression - ie !rule
        if (ruleAST.argument) {
            if (this.rules[ruleAST.argument.name] !== undefined) {
                return [this.rules[ruleAST.argument.name]];
            }
        } else if (this.rules[ruleAST.name] !== undefined) {
            // eslint-disable-line no-else-return
            return [this.rules[ruleAST.name]];
        }
        throw Error(this.getErrorMsgRuleAST(ruleAST, ruleExpr));
    };

    /**
     * Gets error msg for rule ast
     * @param  {String} ruleAST  A rule AST for a single rule.
     * @param {String} ruleExpr the rule expression
     * @returns {String} error msg for RuleAST
     */
    getErrorMsgRuleAST = (ruleAST, ruleExpr) => {
        const name = ruleAST.argument ? ruleAST.argument.name : ruleAST.name;
        const ruleASTObj = ruleAST ? JSON.stringify(ruleAST) : 'ruleAST is undefined';
        return `Rule name ${name} is not found in the rule schema. ruleExpr: ${ruleExpr} ruleAST obj: ${ruleASTObj}`;
    };

    /**
     * Find the matching, passing rule for a 'selectedRule'
     * @param {Object} selectedRules - The set of possible rules to substitute for 'selectedRule'
     * @param {Object} stateValues - The set of stateValues for the elementRule
     * @param {Object} elementRule - The elementRule being evaluated
     * @returns {Object} - The passing selectedRule to substitute into the ruleExpr being evaluated.
     */
    getSelectedRule = (selectedRules, stateValues, elementRule) => {
        const flatRules = Object.keys(selectedRules).reduce((acc, val) => {
            acc[val] = this.extractRules(val)[0];
            acc[selectedRules[val]] = this.extractRules(selectedRules[val])[0];
            return acc;
        }, {});

        for (const ruleID of Object.keys(selectedRules)) {
            const rule = flatRules[ruleID];
            const [ruleExpr, result] = rule.evaluateExpression('', stateValues, elementRule);
            if (result) {
                return flatRules[selectedRules[ruleID]];
            }
        }
        throw Error(`Could not find a passing selected rule: ${elementRule.rule}`);
    };

    /**
     * Evaluate a rule expression by first evaluating all of the rules in the expression,
     * then substituting these values back into the expression and evaluating the result.
     * @param  {Object} elementRule    The element rule to be evaluated.
     * @param  {Object} stateValues    The values from state that are required for evaluating this rule
     * @param  {Boolean} debug         An override for DEBUG_MODE for use in testing
     * @returns {Object}                A map of the expression result, the flattened set of rules,
     *                                 extracted from the expression and the corresponding stateValue for
     *                                 each extracted rule.
     */
    evaluateRuleExpr = (elementRule, stateValues, debug = DEBUG_MODE) => {
        let ruleExpr = elementRule.rule;
        let flatRules = this.extractRules(ruleExpr);

        const rulesResults = {};
        // collect all of the rules used in the ruleExpr so we can return it with the result
        const allRules = [];
        while (flatRules.length > 0) {
            const rule = flatRules.shift();
            allRules.push(rule);
            if (rule.type === 'CompoundRule') {
                flatRules = flatRules.concat(this.extractRules(rule.schema.ruleExpr));
            }

            // Get the result for each rule.
            const evaluated = rule.evaluateExpression(ruleExpr, stateValues, elementRule);

            if (Array.isArray(evaluated)) {
                let partialResult = '';
                [ruleExpr, partialResult] = [...evaluated];

                rulesResults[rule.schema.identifier] = partialResult;
            } else if (typeof evaluated === 'boolean') {
                ruleExpr = evaluated.toString();
            } else {
                ruleExpr = evaluated;
            }
        }

        if (elementRule.selectedRules) {
            try {
                const selectedRule = this.getSelectedRule(elementRule.selectedRules, stateValues, elementRule);
                const [selectedRuleExpr, selectedRuleResult] = selectedRule.evaluateExpression('', stateValues, elementRule);
                ruleExpr = ruleExpr.replace('selectedRules', selectedRuleResult);
                rulesResults.selectedRule = selectedRuleResult;
            } catch (e) {
                ruleExpr = ruleExpr.replace('selectedRules', false);
                rulesResults.selectedRule = false;
            }
        }

        // eval the resulting expression. This should be safe, at this stage the
        // ruleExpr is basically just boolean values and logical operators.
        // @todo: check if there is a safer way to do this / add input sanitization just to be sure.
        // eslint-disable-next-line security/detect-eval-with-expression
        const exprResult = eval(ruleExpr); // eslint-disable-line no-eval
        const props = {};

        rulesCache.cacheElementRuleValues(elementRule, stateValues, rulesResults, exprResult);
        if (!debug) {
            props[elementRule.controls] = { result: exprResult, id: elementRule.id };
        } else {
            // Add all values for easy debugging via the react browser extension when in debug mode
            props[elementRule.controls] = { result: exprResult, id: elementRule.id, elementRule, stateValues, rulesResults };
        }
        return props;
    };

    /**
     * Create a new elementRule and associated Rules, by remapping the given elementRule
     * @param {Object} elementRule - The base elementRule to remap to new id, statekey and ruleExpr
     * @param {String} identifierRemap - The name of the state container for the new elements - eg helpers.sandbox.changeEnergyType
     * @returns {Object} - The remapped elementRule
     */
    remapElementRule = (elementRule, identifierRemap) => {
        const remappedElementRule = { ...elementRule };

        // If elementRule contains a rule that specifies a stateKey (eg isSupplied) we need to remap this stateKey to
        // the stateKey used by the resolved fieldRow / fieldSet that elementRule belongs to, but only if the stateKey
        // matches
        const ruleStateKey = get(remappedElementRule, 'props.stateKey', null);
        if (ruleStateKey) {
            const [stateKeyPrefix, stateKeySuffix] = ruleStateKey.split('.form.');
            remappedElementRule.props.stateKey = `${stateKeyPrefix}.form.${identifierRemap}.${stateKeySuffix}`;
        }

        for (const rule of this.extractRules(elementRule.rule)) {
            if (!rule.schema.stateKey) {
                continue;
            }
            if (rule.schema.stateKey.indexOf('.form.') > 0) {
                const remappedRule = rule.remapRuleToNewID(identifierRemap);
                this.rules[remappedRule.schema.identifier] = remappedRule;
                remappedElementRule.rule = remappedElementRule.rule.replace(rule.schema.identifier, remappedRule.schema.identifier);
            }
        }
        return remappedElementRule;
    };

}

/**
 * Checks if two values are exactly equivalent.
 * @param  {*} actual   The actual value from state.
 * @param  {*} expected The expected value from the schema.
 * @returns {Boolean}    The equivalency between actual and expected.
 */
export const equivalencyEval = (actual, expected) => {
    return actual === expected;
};

/**
 * Checks if actual is greater than expected
 * @param  {Number|Date} actual   The actual value from state.
 * @param  {Number|Date} expected The expected value from the schema.
 * @returns {Boolean}   actual > expected
 */
export const greaterThanEval = (actual, expected) => {
    return actual > expected;
};

/**
 * Checks if actual is less than expected
 * Accepts a filterValue: value or filterValue: { stateKey: 'some.state.key' } }
 * @param  {Number|Date} actual   The actual value from state.
 * @param  {Number|Date} expected The expected value from the schema.
 * @returns {Boolean}   actual < expected
 */
export const lessThanEval = (actual, expected) => {
    return actual < expected;
};

/**
 * Checks if actual is greater than or equal to expected
 * @param  {Number|Date} actual   The actual value from state.
 * @param  {Number|Date} expected The expected value from the schema.
 * @returns {Boolean}   actual >= expected
 */
export const greaterThanOrEqualEval = (actual, expected) => {
    // If actual is null, return true to avoid throwing warnings on empty fields
    if (actual === null) {
        return true;
    }
    return actual >= expected;
};

/**
 * Checks if actual is less than or equal to expected
 * Accepts a filterValue: value or filterValue: { stateKey: 'some.state.key' } }
 * @param  {Number|Date} actual   The actual value from state.
 * @param  {Number|Date} expected The expected value from the schema.
 * @returns {Boolean}   actual <= expected
 */
export const lessThanOrEqualEval = (actual, expected) => {
    // If actual is null, return true to avoid throwing warnings on empty fields
    if (actual === null) {
        return true;
    }
    return actual <= expected;
};

/**
 * Checks if two values are exactly equivalent.
 * @param  {Array|Immutable} actual   The actual value from state.
 * @param  {*} expected The expected value from the schema.
 * @returns {Boolean}    The equivalency between actual and expected.
 */
export const lengthEquivalencyEval = (actual, expected) => {
    // these checks should be Immutable.isImmutable when we upgrade to Immutable 4
    if ((Immutable.List.isList(actual) || Immutable.Map.isMap(actual))) {
        return actual.size === expected;
    }
    if (Array.isArray(actual)) {
        return actual.length === expected;
    }
    return false;
};

/**
 * Checks if actual is less than expected
 * Accepts a filterValue: value or filterValue: { stateKey: 'some.state.key' } }
 * @param  {Array|Immutable} actual   The actual value from state.
 * @param  {Number} expected The expected value from the schema.
 * @returns {Boolean} actual < expected
 */
export const lengthLessThanEval = (actual, expected) => {
    if (Immutable.List.isList(actual) || Immutable.Map.isMap(actual)) {
        return actual.size < expected;
    }
    if (Array.isArray(actual)) {
        return actual.length < expected;
    }
    return false;
};

/**
 * Checks if actual is greater than expected
 * Accepts a filterValue: value or filterValue: { stateKey: 'some.state.key' } }
 * @param  {Array|Immutable} actual   The actual value from state.
 * @param  {Number} expected The expected value from the schema.
 * @returns {Boolean} actual > expected
 */
export const lengthGreaterThanEval = (actual, expected) => {
    if ((Immutable.List.isList(actual) || Immutable.Map.isMap(actual))) {
        return actual.size > expected;
    }
    if (Array.isArray(actual)) {
        return actual.length > expected;
    }
    return false;
};

/**
 * Checks if a value is contained in array.
 * @param  {*} actual         The actual value from state.
 * @param  {Array} expected   The array of possible expected values from the schema.
 * @returns {Boolean}          True if expected contains actual.
 */
export const containsEval = (actual, expected) => {
    return expected.filter((allowedVal) => allowedVal === actual)[0] !== undefined;
};

/**
 * Flips the response of containsEval to resolve a 'notInArray' rule.
 * @param  {*} actual         The actual value from state.
 * @param  {Array} notExpected   The array of not expected values, from the schema.
 * @returns {Boolean}          True if expected does not contain actual.
 */
export const doesNotContainEval = (actual, notExpected) => {
    return !containsEval(actual, notExpected);
};

/**
 * Checks if a value is within a range, including the min or max values
 * If you need a less than or greater than, look at using `lessThan` or `greaterThan` filterOption
 * @param  {*} actual         The actual value from state.
 * @param  {Object} expected  Object containing at least one of min and max values.
 * @returns {Boolean}          True if actual is within the range.
 */
export const rangeEval = (actual, expected) => {
    if (actual === null || actual === undefined) {
        return false;
    }
    if (isNaN(actual)) {
        // if actual is a NaN, then something else has gone wrong and an appropriate validity rule should
        // pick it up
        // The use case for this is checking someones age when given an invalid date. Technically they are less than
        // 100 and more than 17 when the date is NaN, we don't want to trip those rules in that case, but let the
        // validDate rule pick it up
        // @todo: check this and try to find a better pattern
        return true;
    }
    if (expected.max === undefined) {
        return actual >= expected.min;
    } if (expected.min === undefined) {
        return actual <= expected.max;
    }
    return actual >= expected.min && actual <= expected.max;
};

/**
 * Checks if a date is within a range.
 * @param  {Date} actual                The actual value from state.
 * @param  {String} actual.from         The value on state for the from.stateKey
 * @param  {Object|String} actual.min   The minimum date as a string, or a moment-style object
 * @param  {Object|String} actual.max   The maximum date as a string, or a moment-style object
 * @param  {Object} expected            Object containing at least one of min and max values.
 * @returns {Boolean}                   true if actual is within the range.
 */
export const dateRangeEval = (actual, expected) => {
    const parsedExpected = {};
    const fromDate = expected.from ? new Date(expected.from) : new Date();
    if (expected.min) {
        if (typeof expected.min === 'object') {
            parsedExpected.min = addOrSubtractFromDate(fromDate, expected.min);
        } else {
            parsedExpected.min = new Date(expected.min);
            parsedExpected.min.setHours(0, 0, 0, 0);
        }
    }
    if (expected.max) {
        if (typeof expected.max === 'object') {
            parsedExpected.max = addOrSubtractFromDate(fromDate, expected.max);
        } else {
            parsedExpected.max = new Date(expected.max);
            parsedExpected.max.setHours(0, 0, 0, 0);
        }
    }
    const actualDateObj = actual instanceof Date ? actual : new Date(actual);
    // Hours need to be reset to 0 otherwise it will start at the UTC offset number of hours by default
    actualDateObj.setHours(0, 0, 0, 0);
    return rangeEval(actualDateObj, parsedExpected);
};

/**
 * Given a starting date (or the current date), add the fragments provided to generate an outputted date object.
 * Example: 2017-10-31 {years: -20, months: -5, days: -2 } would result in 1997-05-29
 * Note: If you use e.g. getUTCDate, it will get "26" as the day up until after 10am on the 27th.
 * @param {Date} fromDateObj The date we want to calculate the min/max from
 * @param {Object} fragments The moment-style object used to add/subtract from a date.
 * @returns {Date} A date object
 */
export const addOrSubtractFromDate = (fromDateObj, fragments = {}) => {
    const fromYear = fromDateObj.getFullYear();
    const fromMonth = fromDateObj.getMonth();
    const fromDay = fromDateObj.getDate();
    let finalDate = new Date(fromDateObj);
    if (fragments.years) {
        finalDate = new Date(finalDate.setFullYear(fromYear + fragments.years));
    }
    if (fragments.months) {
        finalDate = new Date(finalDate.setMonth(fromMonth + fragments.months));
    }
    if (fragments.days) {
        finalDate = new Date(finalDate.setDate(fromDay + fragments.days));
    }
    // JavaScript compares dates against the getTime() value, so set hours, minutes, seconds, milliseconds to 0.
    finalDate.setHours(0, 0, 0, 0);
    return finalDate;
};

/**
 * Checks if the value is matching the regex
 * @param  {*} actual       The actual value from state.
 * @param  {regex} expected A regex to use in validating actual
 * @returns {Boolean}        The equivalency between actual and expected.
 */
export const regexEval = (actual, expected) => {
    if (!isSuppliedEval(actual)) {
        return false;
    }
    // eslint-disable-next-line security/detect-non-literal-regexp
    const expectedRegex = new RegExp(expected);
    return expectedRegex.test(actual);
};

/**
 * Checks if the value passing the validator function
 * @param  {*} actual       The actual value from state.
 * @param  {Array} expected An array containing a validator function name and an array of args for the function
 * @returns {Boolean}        The equivalency between actual and expected.
 */
export const validatorEval = (actual, expected) => {
    const [validatorFuncName, args = []] = expected;
    // validator can only validate strings - use isSuppliedEval to check that actual is not null or undefined
    if (!isSuppliedEval(actual)) {
        // @todo: double check this
        return false;
    }
    return validator[validatorFuncName](actual, ...args);
};

/**
 * Check if the value is provided and not falsy (except 0)
 * @param {*} actual - The actual value from state.
 * @returns {Boolean} - The equivalency between actual and expected.
 */
export const isSuppliedEval = (actual) => {
    return actual !== undefined && actual !== null && actual !== '';
};

/**
 * Check if the value is a valid date
 * @param {*} actual - The actual value from state.
 * @returns {Boolean} - The equivalency between actual and expected.
 */
export const validDateEval = (actual) => {
    return isValidDateString(actual);
};

/**
 * Validator for dates that only have a month and a year
 * @todo: improve this and check for other cases - preconditions should make this ok for now - current only
 * used for medicare expiry validation for E2E energy
 * @param  {String} actual - The year, month date to validate
 * @returns {Boolean}        The equivalency between actual and expected.
 */
export const validMonthYearDateEval = (actual) => {
    if (!isSuppliedEval(actual)) {
        return false;
    }
    const [year, month] = actual.split('-');
    return year.length === 4 && month.length === 2 && month < 13 && month > 0;
};

/**
 * When actual is an array on state, check if the expectedValue is in the array.
 * @param {Array} actual - The actual value from state which must be an array.
 * @param {String} expected - The value that may or may not be in the array
 * @returns {Boolean} - The equivalency between actual and expected.
 */
export const arrayContainsEval = (actual, expected) => {
    return actual.includes(expected);
};

/**
 * Simple rule factory for constructing rules from a schema.
 */
class RuleFactory {

    /**
     * Sets up the map of evaluation types to evaluators.
     * @constructor
     * @param {Object} customEvaluators - An optional set of custom / vertical specific evaluators
     */
    constructor(customEvaluators = {}) {
        this.evaluators = {
            equals: equivalencyEval,
            lengthLessThan: lengthLessThanEval,
            lengthGreaterThan: lengthGreaterThanEval,
            lengthEquals: lengthEquivalencyEval,
            inArray: containsEval,
            notInArray: doesNotContainEval,
            range: rangeEval,
            dateRange: dateRangeEval,
            regex: regexEval,
            validator: validatorEval,
            isSupplied: isSuppliedEval,
            validDate: validDateEval,
            validMonthYearDate: validMonthYearDateEval,
            greaterThan: greaterThanEval,
            lessThan: lessThanEval,
            greaterThanOrEqual: greaterThanOrEqualEval,
            lessThanOrEqual: lessThanOrEqualEval,
            arrayContains: arrayContainsEval,
            ...customEvaluators
        };
    }

    /**
     * Return a set of Rule objects created from a schema.
     * @param  {Object} schema The schema containing the rule definitions.
     * @returns {Array}         The array of built rules.
     */
    getRules = (schema) => {
        const rules = {};
        for (const ruleSchema of schema.baseRules) {
            // eslint-disable-next-line no-use-before-define
            const rule = new Rule(ruleSchema, this.evaluators[ruleSchema.filterOption]);
            rules[ruleSchema.identifier] = rule;
        }
        for (const ruleSchema of schema.dynamicRules) {
            // eslint-disable-next-line no-use-before-define
            const rule = new DynamicRule(ruleSchema, this.evaluators[ruleSchema.filterOption]);
            rules[ruleSchema.identifier] = rule;
        }
        for (const ruleSchema of schema.compoundRules) {
            // eslint-disable-next-line no-use-before-define
            const rule = new CompoundRule(ruleSchema, null);
            rules[ruleSchema.identifier] = rule;
        }
        return rules;
    };

}

/**
 * Base Rule class
 */
class Rule {

    constructor(schema, evaluator) {
        this.schema = schema;
        this.evaluator = evaluator;
        this.type = 'BaseRule';
        this.id = cuid();
    }

    /**
     * Evaluate the part of the ruleExpr that this rule is responsible for,
     * sub in the result and return the modified ruleExpr.
     * @param  {String} ruleExpr    The current rule expression that this rule is a part of
     * @param  {Object} stateValues The values from the state for all rules in ruleExpr
     * @param  {Object} elementRule The rule definition from an element
     * @returns {String|Boolean} The ruleExpr with the result of this rule subbed in
     */
    evaluateExpression = (ruleExpr, stateValues, elementRule) => {
        let optional = false;
        // @todo: this is a slight hack for optional values that still have validation rules
        // it would be good to figure this out a bit more holistically at some point
        if (elementRule.props && elementRule.props.optional) {
            optional = elementRule.props.optional;
        }

        // get the input value for this rule
        let key = this.schema.stateKey;
        if (key.startsWith('ruleProps:')) {
            // If the rule uses a dynamic stateKey, check that it exists and extract the stateKey from the rule props
            const propsKey = key.split(':')[1];
            if (!get(elementRule, `props.${propsKey}`)) {
                throw Error(`A rule using ruleProps.${propsKey} was specified, but this key is not available on the elementRule - ${JSON.stringify(elementRule)}`);
            }
            key = elementRule.props[propsKey];
        }
        const stateValue = stateValues[key];
        // @todo: Bro to review this logic, do we want to filter out empty string as well, coz BE could return `0` or 0 when
        // @todo: load from web_ctm (different data model). We don't do any validation before we set to store,
        // @todo: so user might get some wrong values in the form, but fortunately our validation will stop them moving forward
        if (optional && !stateValue) {
            // don't try to validate if optional and there is no stateValue
            return true;
        }

        let expected = this.schema.filterValue;
        if (get(this.schema, 'filterValue.stateKey')) {
            expected = stateValues[this.schema.filterValue.stateKey];
        }

        if (this.schema.filterOption === 'range' || this.schema.filterOption === 'dateRange') {
            expected = { ...expected };
            for (const filterRangeName of ['min', 'max', 'from']) {
                // check if the min / max filterValue is a stateKey, if not, it'll just be a number and we don't
                // need to do any extra processing
                if (get(this.schema, `filterValue.${filterRangeName}.stateKey`)) {
                    expected[filterRangeName] = stateValues[this.schema.filterValue[filterRangeName].stateKey];
                }
            }
        }
        // evaluate the rule using the input value
        const result = this.evaluate(stateValue, expected);

        // substitute the result of the rule back into the rule expression and return the result of the rule.
        // eslint-disable-next-line security/detect-non-literal-regexp
        return [ruleExpr.replace(new RegExp(this.schema.identifier, 'g'), result), result];
    };

    /**
     * Evaluate the rule against the supplied stateVal
     * @param  {*} actual - The value from the state that this rule will evaluate
     * @param  {*} expected - The value to evaluate actual against
     * @returns {Boolean}    - The result of the rule evaluation
     */
    evaluate = (actual, expected) => {
        if (!this.evaluator) {
            throw Error(`Tried to call evaluate on a Rule with no evaluator: ${JSON.stringify(this.schema)}`);
        }

        return this.evaluator(actual, expected);
    };

    /**
     * Create a new rule from this rule and remap the stateKey and identifier on the new rule for the provided remap string
     * @param {String} identifierRemap - A string used to create a new identifier and stateKey for the new rule
     * @returns {Rule} - The new rule
     */
    remapRuleToNewID = (identifierRemap) => {
        const remappedSchema = { ...this.schema };
        // use underscores, otherwise the AST tries to interpolate the dots.
        remappedSchema.identifier = `${identifierRemap}.${this.schema.identifier}`.replace(/\./g, '_');

        const [stateKeyPrefix, stateKeySuffix] = this.schema.stateKey.split('.form.');
        remappedSchema.stateKey = `${stateKeyPrefix}.form.${identifierRemap}.${stateKeySuffix}`;
        return new Rule(remappedSchema, this.evaluator);
    };

}

class DynamicRule extends Rule {

    constructor(schema, evaluator) {
        super(schema, evaluator);
        this.type = 'DynamicRule';
    }

    /**
     * Evaluate the part of the ruleExpr that this rule is responsible for,
     * sub in the result and return the modified ruleExpr]
     * @param  {[type]} ruleExpr    The current rule expression that this rule is a part of
     * @param  {[type]} stateValues The values from the state for all rules in ruleExpr
     * @param  {[type]} elementRule The rule definition from an element
     * @returns {String}             The ruleExpr with the result of this rule subbed in
     */
    evaluateExpression = (ruleExpr, stateValues, elementRule) => {
        const stateValue = stateValues[elementRule.props.stateKey];
        const result = this.evaluate(stateValue);

        // substitute the result of the rule back into the rule expression and return the result of the rule.
        // eslint-disable-next-line security/detect-non-literal-regexp
        return [ruleExpr.replace(new RegExp(this.schema.identifier, 'g'), result), result];
    };

}

class CompoundRule extends Rule {

    constructor(schema, evaluator) {
        super(schema, evaluator);
        this.type = 'CompoundRule';
    }

    /**
     * Replaces the compound rule identifier with the ruleExpr so that the ruleExpr is expressed in terms of simple, non-compound rules
     * @param  {[type]} ruleExpr    The current rule expression that this rule is a part of
     * @returns {String}             The ruleExpr with the result of this rule subbed in
     */
    evaluateExpression = (ruleExpr) => {
        // eslint-disable-next-line security/detect-non-literal-regexp
        return ruleExpr.replace(new RegExp(this.schema.identifier, 'g'), this.schema.ruleExpr);
    };

}

export { RulesModel, RuleFactory, Rule, DynamicRule, CompoundRule };
